// Sales Activity TanStack Query hooks — FE-SCRM-024.

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import {
  cancelActivity,
  completeActivity,
  createActivity,
  deleteActivity,
  fetchActivityList,
  patchActivity,
  revertActivityCompletion,
} from "./api";
import type {
  ActivityCreateInput,
  ActivityListQuery,
  ActivityListResponse,
  ActivityPatchInput,
  SalesActivity,
} from "./types";


export const ACTIVITIES_QUERY_KEY = "sales-activities" as const;


export function useActivityListQuery(
  query: ActivityListQuery,
): UseQueryResult<ActivityListResponse> {
  return useQuery({
    queryKey: [ACTIVITIES_QUERY_KEY, "list", query],
    queryFn: ({ signal }) => fetchActivityList(query, { signal }),
  });
}


function _invalidateLists(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: [ACTIVITIES_QUERY_KEY, "list"] });
}


export function useCreateActivityMutation(): UseMutationResult<
  SalesActivity, Error, ActivityCreateInput
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ActivityCreateInput) => createActivity(input),
    onSuccess: () => _invalidateLists(qc),
  });
}


export interface PatchActivityVariables {
  activity_id: string;
  patch: ActivityPatchInput;
}


export function usePatchActivityMutation(): UseMutationResult<
  SalesActivity, Error, PatchActivityVariables
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: PatchActivityVariables) =>
      patchActivity(vars.activity_id, vars.patch),
    onSuccess: () => _invalidateLists(qc),
  });
}


export function useCompleteActivityMutation(): UseMutationResult<
  SalesActivity, Error, { activity_id: string; completed_at?: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { activity_id: string; completed_at?: string }) =>
      completeActivity(vars.activity_id, vars.completed_at),
    onSuccess: () => _invalidateLists(qc),
  });
}


export function useRevertActivityMutation(): UseMutationResult<
  SalesActivity, Error, string
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (activity_id: string) =>
      revertActivityCompletion(activity_id),
    onSuccess: () => _invalidateLists(qc),
  });
}


export function useCancelActivityMutation(): UseMutationResult<
  SalesActivity, Error, string
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (activity_id: string) => cancelActivity(activity_id),
    onSuccess: () => _invalidateLists(qc),
  });
}


export function useDeleteActivityMutation(): UseMutationResult<
  { deleted: true }, Error, string
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (activity_id: string) => deleteActivity(activity_id),
    onSuccess: () => _invalidateLists(qc),
  });
}
